// Admin-only user management. Visible only when role === "admin"; the
// route guard in App.tsx redirects non-admins back to /vpses. The
// underlying endpoints are also extractor-gated server-side so this is
// belt-and-suspenders.

import { useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "../api/admin";
import type { AdminUserRow } from "../api/admin";
import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";

export function AdminUsersPage() {
  const { userId } = useAuth();
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ["admin", "users"],
    queryFn: adminApi.listUsers,
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminApi.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  const [resetTarget, setResetTarget] = useState<AdminUserRow | null>(null);

  return (
    <div className="app-main">
      <h2>Users</h2>
      <p className="muted small">
        Admin can create users, reset their password, and remove them. Admin
        cannot enter any user's server or see their trading data.
      </p>

      <CreateUserForm onCreated={() => qc.invalidateQueries({ queryKey: ["admin", "users"] })} />

      <div className="card">
        <h2>All users</h2>
        {listQ.isLoading && <div className="muted">loading…</div>}
        {listQ.error && (
          <div className="error-banner">
            {listQ.error instanceof ApiError ? listQ.error.message : "failed to load"}
          </div>
        )}
        {listQ.data && (
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {listQ.data.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.username}
                    {u.id === userId && <span className="muted small"> · you</span>}
                  </td>
                  <td>
                    <span className={u.role === "admin" ? "pill ghost" : "pill dim"}>
                      {u.role}
                    </span>
                  </td>
                  <td className="muted small">{u.created_at.replace("T", " ").slice(0, 16)}</td>
                  <td className="right">
                    <button
                      disabled={u.role === "admin"}
                      onClick={() => setResetTarget(u)}
                    >
                      Reset password
                    </button>{" "}
                    <button
                      className="danger"
                      disabled={u.role === "admin" || u.id === userId || remove.isPending}
                      onClick={() => {
                        if (confirm(`Delete user "${u.username}" and ALL their server rows? This cannot be undone.`)) {
                          remove.mutate(u.id);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={() => {
            setResetTarget(null);
          }}
        />
      )}
    </div>
  );
}

// 16-char URL-safe random — well over the 8-char floor enforced server-side.
function generateTempPassword(): string {
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789"; // no 0/O/1/I/l
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  let out = "";
  for (let i = 0; i < buf.length; i++) out += alphabet[buf[i] % alphabet.length];
  return out;
}

function buildInviteText(args: { url: string; username: string; password: string }): string {
  const { url, username, password } = args;
  return [
    `You've been added to ${url}.`,
    ``,
    `URL:      ${url}`,
    `Username: ${username}`,
    `Password: ${password}`,
    ``,
    `First login: rotate the password (Account → Change password).`,
    ``,
    `Setup (~10 min) — you'll need a Tailscale account, a Windows VPS, and`,
    `Rithmic credentials for a funded AMP cash account. Walkthrough:`,
    `https://github.com/veasman/nexus/blob/master/docs/add-user.md`,
  ].join("\n");
}

function CreateUserForm({ onCreated }: { onCreated: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState(() => generateTempPassword());
  const [err, setErr] = useState<string | null>(null);
  // After a successful create we hold onto the temp creds + username so the
  // operator can copy the invite. Cleared on "Create another".
  const [invite, setInvite] = useState<{ username: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      adminApi.createUser({ username: username.trim(), initial_password: password }),
    onSuccess: (row) => {
      setInvite({ username: row.username, password });
      setErr(null);
      onCreated();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "create failed"),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    create.mutate();
  }

  function reset() {
    setUsername("");
    setPassword(generateTempPassword());
    setInvite(null);
    setCopied(false);
    setErr(null);
  }

  async function copyInvite() {
    if (!invite) return;
    const text = buildInviteText({
      url: window.location.origin + "/",
      username: invite.username,
      password: invite.password,
    });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (rare on HTTPS). The textarea below is
      // the manual-select fallback.
    }
  }

  if (invite) {
    const url = window.location.origin + "/";
    const inviteText = buildInviteText({
      url,
      username: invite.username,
      password: invite.password,
    });
    return (
      <div className="card">
        <h2>User created — send this invite</h2>
        <div className="info-banner">
          Created <strong>{invite.username}</strong>. The password below is
          shown <strong>once</strong>; we don't store it in plaintext. Copy
          the whole block and DM it via your password manager.
        </div>
        <textarea
          readOnly
          rows={11}
          className="mono small"
          style={{ width: "100%", marginTop: 8, padding: 8 }}
          value={inviteText}
          onFocus={(e) => e.currentTarget.select()}
        />
        <div className="row" style={{ marginTop: 12, gap: 8 }}>
          <button className="primary" onClick={() => void copyInvite()}>
            {copied ? "Copied ✓" : "Copy invite"}
          </button>
          <button type="button" onClick={reset}>Create another</button>
        </div>
      </div>
    );
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2>Create user</h2>
      <p className="muted small" style={{ marginTop: 0 }}>
        Username + auto-generated temp password. You'll get a copy-paste
        invite block to send them.
      </p>
      {err && <div className="error-banner">{err}</div>}
      <div className="grid grid-2">
        <label>
          <span className="label-text">Username</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
            autoFocus
            placeholder="bob"
          />
        </label>
        <label>
          <span className="label-text">Temp password</span>
          <div className="row" style={{ gap: 6 }}>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mono"
              style={{ flex: 1 }}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => setPassword(generateTempPassword())}
              title="Generate a new random password"
            >
              ↻
            </button>
          </div>
          <span className="label-help">Auto-generated. They rotate it on first sign-in.</span>
        </label>
      </div>
      <button
        className="primary"
        type="submit"
        style={{ marginTop: 12 }}
        disabled={create.isPending || !username.trim() || password.length < 8}
      >
        {create.isPending ? "Creating…" : "Create user"}
      </button>
    </form>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onDone,
}: {
  user: AdminUserRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => adminApi.resetPassword(user.id, pw),
    onSuccess: () => {
      onDone();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "reset failed"),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    reset.mutate();
  }

  return (
    <div className="login-shell" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 10 }}>
      <form className="login-card" onSubmit={submit}>
        <h1 style={{ fontSize: 16 }}>Reset password</h1>
        <div className="subtitle">for {user.username}</div>
        {err && <div className="error-banner">{err}</div>}
        <div className="form-fields">
          <label>
            <span className="label-text">New password</span>
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
              autoComplete="new-password"
            />
          </label>
        </div>
        <button className="primary" type="submit" disabled={reset.isPending || pw.length < 8}>
          {reset.isPending ? "Resetting…" : "Reset"}
        </button>{" "}
        <button type="button" onClick={onClose}>Cancel</button>
      </form>
    </div>
  );
}
